import { readInt32LE, readUInt8 } from "../buffer-io";

/** Decoded GPS fix from a 56-byte AIM/u-blox NAV-SOL record. */
export type GpsSample = {
  timecodeMs: number;
  lat: number;
  lon: number;
  alt: number;
  /** Ground speed in m/s (from ECEF velocity). */
  speed: number;
  fix: number;
  satellites: number;
};

const GPS_RECORD_BYTES = 56;

/** ECEF (meters) to WGS84 lat/lon/alt using Vermeille 2003. */
export function ecefToLla(x: number, y: number, z: number): { lat: number; lon: number; alt: number } {
  const a = 6_378_137;
  const e = 8.181_919_084_261_345e-2;
  const e2 = e * e;
  const e4 = e2 * e2;

  const p = (x * x + y * y) / (a * a);
  const q = ((1 - e2) / (a * a)) * z * z;
  const r = (p + q - e4) / 6;
  const s = (e4 / 4) * (p * q) / (r * r * r);
  const t = (1 + s + Math.sqrt(s * (2 + s))) ** (1 / 3);
  let u = r * (1 + t + 1 / t);
  const v = Math.sqrt(u * u + e4 * q);
  u += v;
  const w = (e2 / 2) * ((u - q) / v);
  const k = Math.sqrt(u + w * w) - w;
  const d = (k * Math.sqrt(x * x + y * y)) / (k + e2);
  const rt = Math.sqrt(d * d + z * z);

  return {
    lat: (180 / Math.PI) * 2 * Math.atan2(z, d + rt),
    lon: (180 / Math.PI) * Math.atan2(y, x),
    alt: ((k + e2 - 1) / k) * rt,
  };
}

/** Fix corrupted upper 16 bits in legacy AIM GPS timecodes. */
export function fixGpsTimecodes(timecodes: number[]): void {
  if (timecodes.length < 2) {
    return;
  }

  let hasBackwards = false;
  for (let index = 1; index < timecodes.length; index += 1) {
    if (timecodes[index]! < timecodes[index - 1]!) {
      hasBackwards = true;
      break;
    }
  }
  if (!hasBackwards) {
    return;
  }

  const base = timecodes[0]! - (timecodes[0]! & 0xffff);
  for (let index = 0; index < timecodes.length; index += 1) {
    timecodes[index] = (timecodes[index]! & 0xffff) + base;
  }

  let previous = timecodes[0]!;
  let cumulative = 0;
  for (let index = 1; index < timecodes.length; index += 1) {
    const masked = timecodes[index]!;
    if (masked < previous) {
      cumulative += 65_536;
    }
    previous = masked;
    timecodes[index] = masked + cumulative;
  }
}

export function decodeGpsTrack(payloads: Buffer[], maxSamples?: number): GpsSample[] {
  if (payloads.length === 0) {
    return [];
  }

  const limit = maxSamples && maxSamples > 0 ? Math.min(maxSamples, payloads.length) : payloads.length;
  const timecodes: number[] = [];
  for (let index = 0; index < limit; index += 1) {
    timecodes.push(readInt32LE(payloads[index]!, 0));
  }
  fixGpsTimecodes(timecodes);

  const samples: GpsSample[] = [];
  for (let index = 0; index < limit; index += 1) {
    const record = payloads[index]!;
    if (record.length < GPS_RECORD_BYTES) {
      continue;
    }

    const x = readInt32LE(record, 16) / 100;
    const y = readInt32LE(record, 20) / 100;
    const z = readInt32LE(record, 24) / 100;
    const { lat, lon, alt } = ecefToLla(x, y, z);

    const vx = readInt32LE(record, 32);
    const vy = readInt32LE(record, 36);
    const vz = readInt32LE(record, 40);
    const speed = Math.sqrt(vx * vx + vy * vy + vz * vz) / 100;

    samples.push({
      timecodeMs: timecodes[index]!,
      lat,
      lon,
      alt,
      speed,
      fix: readUInt8(record, 14),
      satellites: readUInt8(record, 51),
    });
  }

  return samples;
}
